import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import TableContainer from '../TableContainer';
import Pagination10 from '../Pagination10';
import useDefaultModal from '../hooks/useDefaultModal';
import usePagination10 from '../hooks/usePagination10';
import DefaultModal from './DefaultModal';
import ResponsiveShadow from '../ResponsiveShadow';
import PageHeader from '../PageHeader';
import PageContent from '../PageContent';
import PaginationBar from '../PaginationBar';
import DataTable from '../DataTable';
import { formatQuantidade } from '../../../service/utils/formatQuantidade';

function ContratoPedidoItensModal({ isOpen, onClose, contrato, contratoPedido }) {
  const [contratoItens, setContratoItens] = useState([]);
  const [pedidoItensMap, setPedidoItensMap] = useState({});
  const [itensPedidoAnterior, setItensPedidoAnterior] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [editedItems, setEditedItems] = useState({});
  const [possuiPedidoPosterior, setPossuiPedidoPosterior] = useState(false);
  const possuiEntrega = contratoPedido?.status && contratoPedido.status !== 'PENDENTE';
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (isOpen && contrato && contratoPedido) {
      carregarTodosDados();
    }
  }, [isOpen, contrato, contratoPedido]);

  async function carregarTodosDados() {
    setLoading(true);
    try {
      const itensContratoResponse = await axios.get(
        `${API_BASE_URL.CONTRATO_ITEM}/contrato/${contrato.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const itensPedidoResponse = await axios.get(
        `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/contrato-pedido/${contrato.id}/${contratoPedido.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const pedidosResponse = await axios.get(
        `${API_BASE_URL.CONTRATO_PEDIDO}/contrato/${contrato.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const pedidos = pedidosResponse.data;

      const pedidosPosteriores = pedidos.filter(p => p.id > contratoPedido.id);
      setPossuiPedidoPosterior(pedidosPosteriores.length > 0);

      const pedidosAnteriores = pedidos
        .filter(p => p.id < contratoPedido.id)
        .sort((a, b) => b.id - a.id);

      let itensAnterioresMap = {};
      if (pedidosAnteriores.length > 0) {
        for (const pedidoAnterior of pedidosAnteriores) {
          const itensAnterioresResponse = await axios.get(
            `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/contrato-pedido/${contrato.id}/${pedidoAnterior.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          itensAnterioresResponse.data.forEach(item => {
            if (!itensAnterioresMap[item.idContratoItem] ||
                pedidoAnterior.id > itensAnterioresMap[item.idContratoItem].idPedido) {
              itensAnterioresMap[item.idContratoItem] = {
                ...item,
                idPedido: pedidoAnterior.id
              };
            }
          });
        }
      }

      setContratoItens(itensContratoResponse.data);
      setItensPedidoAnterior(Object.values(itensAnterioresMap));

      const map = {};
      itensPedidoResponse.data.forEach(item => {
        map[item.idContratoItem] = item;
      });
      setPedidoItensMap(map);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showDefaultModal('error', 'Erro ao carregar dados dos itens.');
    } finally {
      setLoading(false);
    }
  }

  function getQtdeAcumAnterior(idItem) {
    const itemAnterior = itensPedidoAnterior.find(i => i.idContratoItem === idItem);
    return itemAnterior ? itemAnterior.qtdeAcumAtual : 0;
  }

  function getQtdePedida(idItem) {
    if (editedItems[idItem] !== undefined) {
      return editedItems[idItem];
    }
    return pedidoItensMap[idItem]?.qtdePedida || 0;
  }

  function handleQtdeChange(idItem, value) {
    const qtde = parseFloat(value) || 0;
    setEditedItems(prev => ({ ...prev, [idItem]: qtde }));
  }

  async function handleSalvarTodos() {
    if (possuiPedidoPosterior) {
      showDefaultModal('error', 'Não é possível editar este pedido pois já existem pedidos posteriores.');
      return;
    }
    if (possuiEntrega) {
      showDefaultModal('error', 'Não é possível editar este pedido pois já possui entrega registrada.');
      return;
    }

    const itensParaSalvar = [];

    for (const item of contratoItens) {
      const qtdePedida = getQtdePedida(item.id);
      const qtdeAcumAnterior = getQtdeAcumAnterior(item.id);
      const qtdeAcumAtual = qtdeAcumAnterior + qtdePedida;
      const itemExistente = pedidoItensMap[item.id];

      if (qtdeAcumAtual > item.quantidade) {
        showDefaultModal(
          'error',
          `Item ${item.id} - ${item.descricao}: A quantidade total (${formatQuantidade(qtdeAcumAtual)}) não pode exceder a quantidade contratada (${formatQuantidade(item.quantidade)}).`
        );
        return;
      }

      if (editedItems[item.id] !== undefined || qtdePedida > 0) {
        itensParaSalvar.push({ item, qtdePedida, qtdeAcumAnterior, qtdeAcumAtual, itemExistente });
      }
    }

    if (itensParaSalvar.length === 0) {
      showDefaultModal('info', 'Nenhuma alteração para salvar.');
      return;
    }

    try {
      for (const dados of itensParaSalvar) {
        const payload = {
          idContrato: contrato.id,
          idContratoPedido: contratoPedido.id,
          idContratoItem: dados.item.id,
          qtdeAcumAnterior: dados.qtdeAcumAnterior,
          qtdePedida: dados.qtdePedida,
          qtdeAcumAtual: dados.qtdeAcumAtual,
        };

        if (dados.qtdePedida === 0 && dados.itemExistente) {
          await axios.delete(
            `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/${contrato.id}/${contratoPedido.id}/${dados.item.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else if (dados.qtdePedida > 0) {
          if (dados.itemExistente) {
            await axios.put(
              `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/${contrato.id}/${contratoPedido.id}/${dados.item.id}`,
              payload,
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            await axios.post(API_BASE_URL.CONTRATO_PEDIDO_ITEM, payload, {
              headers: { Authorization: `Bearer ${token}` },
            });
          }
        }
      }

      showDefaultModal('success', `${itensParaSalvar.length} item(ns) salvo(s) com sucesso!`);
      setEditedItems({});
      await carregarTodosDados();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar itens:', error);
      showDefaultModal('error', 'Erro ao salvar itens. Tente novamente.');
    }
  }

  async function handleSalvarItem(contratoItem) {
    if (possuiPedidoPosterior) {
      showDefaultModal('error', 'Não é possível editar este pedido pois já existem pedidos posteriores.');
      return;
    }
    if (possuiEntrega) {
      showDefaultModal('error', 'Não é possível editar este pedido pois já possui entrega registrada.');
      return;
    }

    const qtdePedida = getQtdePedida(contratoItem.id);
    const qtdeAcumAnterior = getQtdeAcumAnterior(contratoItem.id);
    const qtdeAcumAtual = qtdeAcumAnterior + qtdePedida;

    if (qtdeAcumAtual > contratoItem.quantidade) {
      showDefaultModal(
        'error',
        `A quantidade total (${formatQuantidade(qtdeAcumAtual)}) não pode exceder a quantidade contratada (${formatQuantidade(contratoItem.quantidade)}).`
      );
      return;
    }

    const payload = {
      idContrato: contrato.id,
      idContratoPedido: contratoPedido.id,
      idContratoItem: contratoItem.id,
      qtdeAcumAnterior,
      qtdePedida,
      qtdeAcumAtual,
    };

    try {
      const itemExistente = pedidoItensMap[contratoItem.id];

      if (qtdePedida === 0 && itemExistente) {
        await axios.delete(
          `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/${contrato.id}/${contratoPedido.id}/${contratoItem.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showDefaultModal('success', 'Item removido do pedido!');
      } else if (qtdePedida > 0) {
        if (itemExistente) {
          await axios.put(
            `${API_BASE_URL.CONTRATO_PEDIDO_ITEM}/${contrato.id}/${contratoPedido.id}/${contratoItem.id}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          showDefaultModal('success', 'Item atualizado com sucesso!');
        } else {
          await axios.post(API_BASE_URL.CONTRATO_PEDIDO_ITEM, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          showDefaultModal('success', 'Item adicionado ao pedido!');
        }
      }

      setEditedItems(prev => {
        const newEdited = { ...prev };
        delete newEdited[contratoItem.id];
        return newEdited;
      });
      await carregarTodosDados();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      showDefaultModal('error', 'Erro ao salvar item. Tente novamente.');
    }
  }

  function calcularTotalPedido() {
    return contratoItens.reduce((total, item) => {
      const qtdePedida = getQtdePedida(item.id);
      return total + qtdePedida * (item.valorUnit || 0);
    }, 0);
  }

  const itensFiltrados = contratoItens.filter(item => {
    const termo = filtro.toLowerCase();
    return (
      item.descricao?.toLowerCase().includes(termo) ||
      item.marca?.toLowerCase().includes(termo) ||
      item.id.toString().includes(termo)
    );
  });

  const totalItems = itensFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const currentItems = itensFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.5rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h6 className="modal-title mb-0">
              <i className="bi bi-list-check me-2"></i>
              Itens do Pedido Nº {contratoPedido?.id} - Contrato {contrato?.numContrato}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} title="Fechar"></button>
          </div>
          <PageContent>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleSalvarTodos}
                  disabled={possuiPedidoPosterior || possuiEntrega || Object.keys(editedItems).length === 0}
                  title={
                    possuiPedidoPosterior
                      ? 'Pedido bloqueado - existem pedidos posteriores'
                      : possuiEntrega
                      ? 'Pedido bloqueado - já possui entrega registrada'
                      : Object.keys(editedItems).length === 0
                      ? 'Nenhuma alteração para salvar'
                      : 'Salvar todas as alterações'
                  }
                >
                  <i className="bi bi-save me-1"></i>
                  Salvar Todos
                </button>
              </div>
              <div className="col-md-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filtrar por ID, descrição ou marca..."
                    value={filtro}
                    onChange={e => setFiltro(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="text-center align-middle">ID</th>
                  <th scope="col" className="text-center align-middle">Descrição</th>
                  <th scope="col" className="text-center align-middle">Marca</th>
                  <th scope="col" className="text-center align-middle">Unid.</th>
                  <th scope="col" className="text-center align-middle">Qtd. Contrat.</th>
                  <th scope="col" className="text-center align-middle">Qtd. Acum. Anterior</th>
                  <th scope="col" className="text-center align-middle">Qtd. Pedida</th>
                  <th scope="col" className="text-center align-middle">Qtd. Acum. Atual</th>
                  <th scope="col" className="text-center align-middle">Saldo</th>
                  <th scope="col" className="text-center align-middle">Valor Unit.</th>
                  <th scope="col" className="text-center align-middle">Total</th>
                </tr>}>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-3 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                        Nenhum item encontrado.
                      </td>
                    </tr>
                  ) : (
                    currentItems.map(item => {
                      const qtdeAcumAnterior = getQtdeAcumAnterior(item.id);
                      const qtdePedida = getQtdePedida(item.id);
                      const qtdeAcumAtual = qtdeAcumAnterior + qtdePedida;
                      const saldo = item.quantidade - qtdeAcumAtual;
                      const total = qtdePedida * (item.valorUnit || 0);
                      const excedeLimite = qtdeAcumAtual > item.quantidade;

                      return (
                        <tr key={item.id} className={excedeLimite ? 'table-danger' : ''}>
                          <td className="px-2">{item.id}</td>
                          <td className="px-2">{item.descricao}</td>
                          <td className="px-2">{item.marca || '-'}</td>
                          <td className="px-2">{item.unidade || '-'}</td>
                          <td className="px-2 text-end"><strong>{formatQuantidade(item.quantidade)}</strong></td>
                          <td className="px-2 text-end">{formatQuantidade(qtdeAcumAnterior)}</td>
                          <td className="px-2 text-end">
                            <input
                              type="number"
                              className="form-control form-control-sm text-end py-0"
                              style={{ width: '80px', display: 'inline-block', height: 'calc(1.5em + 0.2rem)' }}
                              min="0"
                              step="0.01"
                              max={item.quantidade - qtdeAcumAnterior}
                              value={qtdePedida}
                              onChange={e => handleQtdeChange(item.id, e.target.value)}
                               disabled={possuiPedidoPosterior || possuiEntrega}
                            />
                          </td>
                          <td className="px-2 text-end">
                            <strong className={excedeLimite ? 'text-danger' : ''}>{formatQuantidade(qtdeAcumAtual)}</strong>
                          </td>
                          <td className={`px-2 text-end fw-bold ${saldo === 0 ? 'text-success' : saldo < 0 ? 'text-danger' : 'text-primary'}`}>
                            {formatQuantidade(saldo)}
                          </td>
                          <td className="px-2 text-end">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorUnit || 0)}
                          </td>
                          <td className="px-2 text-end">
                            <strong>
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(total)}
                            </strong>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {currentItems.length > 0 && (
                  <tfoot className="fw-bold" style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f8f9fa', isolation: 'isolate' }}>
                    <tr>
                      <td colSpan="7" className="px-2 text-start">
                        {(possuiPedidoPosterior || possuiEntrega) && (
                          <span className="bg-warning p-1 d-block" style={{ fontSize: '0.8rem', borderRadius: '4px' }}>
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            <strong>Atenção:</strong> Este pedido não pode ser editado{possuiPedidoPosterior ? ' pois já existem pedidos posteriores que dependem dos valores acumulados.' : ' pois já possui entrega registrada.'}
                          </span>
                        )}
                      </td>
                      <td colSpan="3" className="px-2 text-end">TOTAL DO PEDIDO:</td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalPedido())}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </DataTable>
            </TableContainer>
          </PageContent>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

export default ContratoPedidoItensModal;
